import type { Directive } from 'vue';

/**
 * `v-tip="'Arşive kaldır'"` — a label that appears beside a control on hover and
 * on keyboard focus. Every icon-only button carries one: the people using the
 * console are not expected to know what a symbol means.
 *
 * One shared element serves the whole page. The text also becomes the control's
 * accessible name when it has none, so screen readers say the same thing.
 *
 * Phones have no hover: there a press-and-hold shows the label for a few seconds,
 * and the tap that ends the hold does not also press the button.
 */

const SHOW_DELAY = 250;
let bubble: HTMLDivElement | null = null;
let owner: HTMLElement | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;

const texts = new WeakMap<HTMLElement, string>();

const HOLD = 450;
const HOLD_VISIBLE = 2600;
let holdTimer: ReturnType<typeof setTimeout> | undefined;
let held: HTMLElement | null = null;

function element(): HTMLDivElement {
  if (!bubble) {
    bubble = document.createElement('div');
    bubble.className = 'tip';
    bubble.setAttribute('role', 'tooltip');
    bubble.id = 'tip';
    document.body.appendChild(bubble);
  }
  return bubble;
}

function place(target: HTMLElement) {
  const tip = element();
  const box = target.getBoundingClientRect();
  const width = tip.offsetWidth;
  const height = tip.offsetHeight;
  const gap = 8;
  // Above by default; below when there is no room (e.g. buttons at the top of the screen).
  const above = box.top - height - gap >= 4;
  const top = above ? box.top - height - gap : box.bottom + gap;
  const left = Math.min(
    Math.max(4, box.left + box.width / 2 - width / 2),
    window.innerWidth - width - 4,
  );
  tip.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`;
  tip.dataset['side'] = above ? 'top' : 'bottom';
}

function show(target: HTMLElement, immediate = false) {
  const text = texts.get(target);
  if (!text) return;
  clearTimeout(timer);
  timer = setTimeout(
    () => {
      const tip = element();
      tip.textContent = text;
      owner = target;
      target.setAttribute('aria-describedby', tip.id);
      tip.classList.add('is-visible');
      place(target);
    },
    immediate ? 0 : SHOW_DELAY,
  );
}

function hide() {
  clearTimeout(timer);
  owner?.removeAttribute('aria-describedby');
  owner = null;
  bubble?.classList.remove('is-visible');
}

function onEnter(this: HTMLElement) {
  // A tap fires mouseenter too; on touch the label waits for a hold instead.
  if (held !== null || holdTimer !== undefined) return;
  show(this);
}
function onPointerDown(this: HTMLElement, e: PointerEvent) {
  if (e.pointerType !== 'touch') return;
  const el = this;
  held = null;
  clearTimeout(holdTimer);
  holdTimer = setTimeout(() => {
    held = el;
    show(el, true);
    clearTimeout(timer);
    timer = setTimeout(hide, HOLD_VISIBLE);
  }, HOLD);
}
function onPointerEnd() {
  clearTimeout(holdTimer);
  holdTimer = undefined;
}
function onClick(this: HTMLElement, e: MouseEvent) {
  if (held === this) {
    // The finger lifted after showing the label: that was a question, not a press.
    e.preventDefault();
    e.stopImmediatePropagation();
    held = null;
    return;
  }
  hide();
}
function onContextMenu(this: HTMLElement, e: Event) {
  // Long-pressing a button should explain it, not open the system menu.
  if (texts.get(this)) e.preventDefault();
}
function onFocus(this: HTMLElement) {
  // Keyboard focus only; a mouse click focusing the button should not pop a label.
  if (this.matches(':focus-visible')) show(this, true);
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') hide();
}

function setText(el: HTMLElement, text: string | null | undefined) {
  texts.set(el, text ?? '');
  // The tooltip names the control; the browser's own title would show a second one.
  el.removeAttribute('title');
  // An icon-only button gets the text as its name. Form fields already have a
  // visible label; there the tooltip only describes (aria-describedby on show).
  const isField = el.matches('input, select, textarea');
  if (text && !isField && !el.hasAttribute('aria-label') && !el.textContent?.trim()) {
    el.setAttribute('aria-label', text);
  }
  if (owner === el && bubble) {
    bubble.textContent = text ?? '';
    place(el);
  }
}

export const vTip: Directive<HTMLElement, string | null | undefined> = {
  mounted(el, { value }) {
    setText(el, value);
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', hide);
    el.addEventListener('focus', onFocus);
    el.addEventListener('blur', hide);
    // Capture, so a hold can stop the click before the button's own handler.
    el.addEventListener('click', onClick, true);
    el.addEventListener('keydown', onKey);
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointerup', onPointerEnd);
    el.addEventListener('pointercancel', onPointerEnd);
    el.addEventListener('pointerleave', onPointerEnd);
    el.addEventListener('contextmenu', onContextMenu);
  },
  updated(el, { value, oldValue }) {
    if (value !== oldValue) setText(el, value);
  },
  beforeUnmount(el) {
    if (owner === el) hide();
    if (held === el) held = null;
    el.removeEventListener('mouseenter', onEnter);
    el.removeEventListener('mouseleave', hide);
    el.removeEventListener('focus', onFocus);
    el.removeEventListener('blur', hide);
    el.removeEventListener('click', onClick, true);
    el.removeEventListener('keydown', onKey);
    el.removeEventListener('pointerdown', onPointerDown);
    el.removeEventListener('pointerup', onPointerEnd);
    el.removeEventListener('pointercancel', onPointerEnd);
    el.removeEventListener('pointerleave', onPointerEnd);
    el.removeEventListener('contextmenu', onContextMenu);
  },
};

// Scrolling moves the control away from its label.
if (typeof window !== 'undefined') {
  window.addEventListener('scroll', hide, { passive: true, capture: true });
}
